import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import classNames from 'classnames'

// components
import { MultiSelect } from 'primereact/multiselect'
import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import MultiSelectFooter from 'primereact-raytm-theme/MultiSelectFooter'
import { Dropdown } from 'primereact/dropdown'
import { optionRow } from '@aytm/app/stats/page/question-views/components/controls/OptionLabel'
import { InputSwitch } from 'primereact/inputswitch'

import ToggleTriplet from '@aytm/app/stats/components/toggle-triplet/ToggleTriplet'

// styles
import style from '@aytm/app/stats/page/question-views/bpm/components/tv/styles/Controls.module.scss'

import {
  prepareOptionsChartLegends,
  prepareOptionsForControls
} from '@aytm/app/stats/page/question-views/bpm/lib/utils'

import { PLOT_VALUES } from '@aytm/app/stats/page/question-views/bpm/components/tv/lib/constants'
import {
  getDefaultValues,
  getTvElevationsRepresentOptions,
  getHiddenEntityPlaceholder,
  getHiddenAttributesPlaceholder
} from '@aytm/app/stats/page/question-views/bpm/components/tv/lib/utils'
import { NO_PRINT } from '@aytm/app/stats/page/question-views/components/lib/constants'

// Entities/Attributes switch, mounted by Block in the header row next to the mode tabs.
// `defaultValues` here is the tv shape (Block's defaultValues.t).
export const PlotByToggle = ({ tvPlotByValue, defaultValues, onModelChange }) => {
  const onPlotChange = value =>
    onModelChange({ tvPlotByValue: value, tvElevationsRepresentValue: defaultValues['e'][value] })

  return (
    <ToggleTriplet
      className={style.triplet}
      name="bpm-tv-plot-by"
      leftLabel="Entities"
      rightLabel="Attributes"
      activeLeft={PLOT_VALUES.e === tvPlotByValue}
      onLeftClick={() => onPlotChange(PLOT_VALUES.e)}
      onRightClick={() => onPlotChange(PLOT_VALUES.a)}
    >
      {/* the side captions live in the shared triplet; on means the right one, Attributes */}
      <InputSwitch
        aria-label="Plot by attributes"
        checked={PLOT_VALUES.a === tvPlotByValue}
        onChange={event => onPlotChange(event.value ? PLOT_VALUES.a : PLOT_VALUES.e)}
      />
    </ToggleTriplet>
  )
}

PlotByToggle.displayName = 'BpmTvPlotByToggle'

PlotByToggle.propTypes = {
  tvPlotByValue: PropTypes.string,
  defaultValues: PropTypes.object.isRequired,
  onModelChange: PropTypes.func.isRequired
}

const Controls = ({
  answer,
  tvPlotByValue,
  tvElevationsRepresentValue,
  defaultPlotByValue,
  tvBrandsValue,
  tvAttributesValue,
  questionVariants,
  chartLegendMode,
  batches,
  onModelChange
}) => {
  const defaultValues = getDefaultValues({ defaultPlotByValue, questionVariants, batches })
  const tvElevationsRepresentOptions = getTvElevationsRepresentOptions({
    tvPlotByValue,
    answer,
    questionVariants,
    batches,
    chartLegendMode
  })

  const entitiesOptions = prepareOptionsChartLegends(questionVariants, chartLegendMode)
  const attributesOptions = prepareOptionsForControls(batches)

  // the value comes off the event itself: the list's own Deselect all sends it without a field name
  const onFilterChange = name => event => onModelChange({ [name]: event.value })

  const onRepresentFilterChange = event => {
    const { name, value } = event.target
    onModelChange({
      [name]: Number(value) ? value : defaultValues['e'][tvPlotByValue]
    })
  }

  return (
    <div className={`${style.row} ${NO_PRINT}`}>
      <MultiSelect
        data-testid="stat-bpm-tv-entities"
        name="tvBrandsValue"
        className={classNames(style.select, 'p-multiselect-rounded')}
        placeholder={`Filter by entities: ${getHiddenEntityPlaceholder(tvBrandsValue, entitiesOptions)}`}
        fixedPlaceholder
        value={tvBrandsValue}
        options={entitiesOptions}
        optionValue="value"
        optionLabel="text"
        itemTemplate={optionRow('bpm-tv-entities')}
        panelHeaderTemplate={MultiSelectHeader}
        panelFooterTemplate={MultiSelectFooter}
        removeIcon={null}
        onChange={onFilterChange('tvBrandsValue')}
      />
      <MultiSelect
        data-testid="stat-bpm-tv-attributes"
        name="tvAttributesValue"
        className={classNames(style.select, 'p-multiselect-rounded')}
        placeholder={`Filter by attributes: ${getHiddenAttributesPlaceholder(tvAttributesValue, attributesOptions)}`}
        fixedPlaceholder
        value={tvAttributesValue}
        options={attributesOptions}
        optionValue="value"
        optionLabel="text"
        itemTemplate={optionRow('bpm-tv-attributes')}
        panelHeaderTemplate={MultiSelectHeader}
        panelFooterTemplate={MultiSelectFooter}
        removeIcon={null}
        onChange={onFilterChange('tvAttributesValue')}
      />
      <Dropdown
        className={classNames(style.selectWide, 'p-dropdown-rounded')}
        aria-label="Elevations represent"
        ariaLabel="Elevations represent"
        name="tvElevationsRepresentValue"
        value={tvElevationsRepresentValue}
        options={tvElevationsRepresentOptions}
        optionValue="value"
        optionLabel="text"
        optionDisabled="disabled"
        fixedDropdownDir="bottom"
        itemTemplate={optionRow('bpm-tv-elevations')}
        valueTemplate={option => (
          <>
            Elevations represent: <b>{option?.text}</b>
          </>
        )}
        onChange={onRepresentFilterChange}
      />
    </div>
  )
}

Controls.displayName = 'BpmControlsTv'

Controls.propTypes = {
  answer: answerPropType,
  chartLegendMode: PropTypes.string,
  defaultPlotByValue: PropTypes.string,
  tvPlotByValue: PropTypes.string,
  tvElevationsRepresentValue: PropTypes.string,
  questionVariants: PropTypes.arrayOf(PropTypes.object).isRequired,
  batches: PropTypes.arrayOf(PropTypes.object).isRequired,
  tvBrandsValue: PropTypes.arrayOf(PropTypes.string).isRequired,
  tvAttributesValue: PropTypes.arrayOf(PropTypes.string).isRequired,
  onModelChange: PropTypes.func.isRequired
}

export default Controls
